import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchAuditedTableData, updateAuditedTableData } from '../../../../store/company/action';
import { getTimeStamp } from '../../../../utils/dateUtils';
import { removeKeyAndConvertIdToNumber } from '../../../../utils/utils';
import { DATA_FETCH_STATUS } from '../../../../store/dashboard/constants';

const AUDITED_POLL_INTERVAL_MS = 5000;

interface UseAuditedStatementDataParams {
    orgId: string;
    /** The backend `misDisplayName` this instance owns (e.g. 'Balance Sheet'). Empty until resolved. */
    metric: string;
}

/**
 * Owns fetch + drill-down + polling + classification-save state for ONE audited-financials
 * statement. Extracted out of AuditedFinancialsVisualisation (RECUR-27010) so the redesigned
 * page can run three independent instances side by side (Balance Sheet / P&L / Cash Flow)
 * while the legacy single-metric report view (AicaPDFReport, driven by `selectedMetric`)
 * keeps using exactly one — same fetch/poll/save behavior either way.
 */
export function useAuditedStatementData({ orgId, metric }: UseAuditedStatementDataParams) {
    const dispatch = useDispatch();
    const queryParams = useParams();

    const [tableData, setTableData] = useState<any>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [status, setStatus] = useState<string>('');
    const [tableType, setTableType] = useState<string>('');
    const [rawTableData, setRawTableData] = useState<any>({});
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [changedData, setChangedData] = useState<any>([]);

    const pollingIntervalRef = useRef<any>(null);
    const natureRef = useRef<string | undefined>(undefined);

    const fetchTableData = useCallback(
        (nature?: string, isPolling = false) => {
            if (!metric || !orgId) return;
            natureRef.current = nature;
            if (!isPolling) setLoading(true);
            dispatch(
                fetchAuditedTableData(
                    { type: 'auditedfinancials', mis: metric, orgId, requestId: getTimeStamp(), raw: false, nature },
                    {
                        onError: () => setLoading(false),
                        onSuccess: (data: any, statusRes: any) => {
                            setStatus(statusRes?.auditedFinancialsStatus);
                            setLoading(false);
                            setTableData(data);
                        },
                    },
                ),
            );
        },
        [dispatch, metric, orgId],
    );

    const fetchRawData = useCallback(
        (row: { [key: string]: string }, header: { [key: string]: string }) => {
            if (header.name !== 'Classification' || !row?.[header.key] || row.isAggregated) return;
            setLoading(true);
            setTableType(row.classificationName);
            dispatch(
                fetchAuditedTableData(
                    {
                        type: 'auditedfinancials',
                        mis: metric,
                        orgId,
                        requestId: getTimeStamp(),
                        classificationId: row.classificationId,
                        raw: true,
                        nature: natureRef.current,
                    },
                    {
                        onError: () => setLoading(false),
                        onSuccess: (data: any) => {
                            setLoading(false);
                            setRawTableData(data);
                        },
                    },
                ),
            );
        },
        [dispatch, metric, orgId],
    );

    const resetDrillDown = useCallback(() => {
        setTableType('');
        setIsEditing(false);
        setChangedData([]);
    }, []);

    const handleSave = useCallback(() => {
        dispatch(
            updateAuditedTableData({
                orgId: queryParams.id ?? '',
                metrics: metric,
                changedData: removeKeyAndConvertIdToNumber(changedData, 'name'),
                source: 'AicaAuditedFinancials',
                onSuccess: () => {
                    fetchTableData(natureRef.current);
                    resetDrillDown();
                    toast.success('Data saved successfully');
                },
                onError: () => {
                    toast.error('Failed to save data');
                },
            }),
        );
    }, [dispatch, queryParams.id, metric, changedData, fetchTableData, resetDrillDown]);

    // Initial fetch — fires once the (possibly async-resolved) metric name is known, and
    // again if it ever changes (the legacy report-mode metric dropdown can still switch it).
    useEffect(() => {
        if (metric) fetchTableData(natureRef.current);
        // fetchTableData is a stable useCallback memoised on [dispatch, metric, orgId] — `metric`
        // is already a dep here, so its identity is covered; omitting it avoids a redundant re-run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metric]);

    // Poll while the backend is still generating this statement.
    useEffect(() => {
        if (status === DATA_FETCH_STATUS.PROCESSING) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = setInterval(() => {
                fetchTableData(natureRef.current, true);
            }, AUDITED_POLL_INTERVAL_MS);
        } else {
            clearInterval(pollingIntervalRef.current);
        }
        return () => clearInterval(pollingIntervalRef.current);
    }, [status, fetchTableData]);

    return {
        tableData,
        setTableData,
        loading,
        status,
        tableType,
        setTableType,
        rawTableData,
        isEditing,
        setIsEditing,
        changedData,
        setChangedData,
        fetchTableData,
        fetchRawData,
        resetDrillDown,
        handleSave,
    };
}
